import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import React from "react";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Copy,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  getWebhookDebug,
  type WebhookEventRow,
} from "@/lib/webhook-debug.functions";

export const Route = createFileRoute("/admin_/webhooks")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Webhook Debug — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: WebhooksDebugPage,
});

const FILTERS = ["all", "ok", "error", "signature_failures", "duplicates"] as const;
type Filter = (typeof FILTERS)[number];

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
    error: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
        map[status] ?? "bg-muted text-foreground border-border"
      }`}
    >
      {status === "ok" ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : status === "error" ? (
        <AlertTriangle className="h-3 w-3" />
      ) : null}
      {status}
    </span>
  );
}

function formatTs(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function WebhooksDebugPage() {
  const fetchDebug = useServerFn(getWebhookDebug);
  const [filter, setFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["webhook-debug"],
    queryFn: () => fetchDebug({ data: undefined as never }),
    refetchInterval: 15_000,
  });

  const filtered = useMemo<WebhookEventRow[]>(() => {
    const rows = data?.events ?? [];
    const byFilter = rows.filter((e) => {
      switch (filter) {
        case "ok":
          return e.status === "ok";
        case "error":
          return e.status === "error";
        case "signature_failures":
          return e.event_type === "signature_verification_failed";
        case "duplicates":
          return e.duplicate === true;
        default:
          return true;
      }
    });
    if (!q.trim()) return byFilter;
    const needle = q.trim().toLowerCase();
    return byFilter.filter((e) =>
      [e.event_type, e.summary, e.reference, e.source]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(needle)),
    );
  }, [data, filter, q]);

  const stats = data?.stats;
  const processed = data?.processed ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <Link
            to="/admin"
            className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
          </Link>
          <h1 className="font-serif text-2xl text-foreground md:text-3xl">
            Webhook debug
          </h1>
          <p className="text-sm text-muted-foreground">
            Recent Stripe webhook deliveries, signature results, and
            idempotency outcomes.
          </p>
          <Link
            to="/admin/paypal/replay"
            className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground underline hover:text-foreground"
          >
            PayPal webhook replay (safe dry run)
          </Link>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm hover:bg-muted"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-6">
        <StatCard label="Recent" value={stats?.total ?? 0} />
        <StatCard label="Last 24h" value={stats?.last_24h ?? 0} />
        <StatCard
          label="Succeeded"
          value={stats?.ok ?? 0}
          tone="text-emerald-700"
        />
        <StatCard
          label="Errors"
          value={stats?.error ?? 0}
          tone="text-red-700"
        />
        <StatCard
          label="Sig failures"
          value={stats?.signature_failures ?? 0}
          tone="text-red-700"
        />
        <StatCard label="Duplicates" value={stats?.duplicates ?? 0} />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              filter === f
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-background text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search summary, event id, type…"
          className="ms-auto w-full max-w-xs rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-foreground sm:w-64"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-start text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Summary</th>
              <th className="px-3 py-2">Reference</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-10 text-center text-muted-foreground"
                >
                  No matching events.
                </td>
              </tr>
            ) : (
              filtered.map((e) => {
                const isOpen = expanded === e.id;
                return (
                  <React.Fragment key={e.id}>
                    <tr
                      key={e.id}
                      onClick={() => setExpanded(isOpen ? null : e.id)}
                      className="cursor-pointer border-t border-border hover:bg-muted/30"
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                        {formatTs(e.created_at)}
                      </td>
                      <td className="px-3 py-2 text-xs">{e.source}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {e.event_type}
                        {e.duplicate ? (
                          <span className="ms-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                            dup
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={e.status} />
                      </td>
                      <td className="px-3 py-2 text-xs text-foreground">
                        {e.summary ?? "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                        {e.reference ?? "—"}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className="border-t border-border bg-muted/20">
                        <td colSpan={6} className="px-3 py-3">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                              details
                            </span>
                            {e.details_json ? (
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  navigator.clipboard
                                    .writeText(e.details_json!)
                                    .then(() =>
                                      toast.success("Details copied"),
                                    );
                                }}
                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                              >
                                <Copy className="h-3 w-3" /> Copy
                              </button>
                            ) : null}
                          </div>
                          <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-background p-3 text-[11px] leading-snug">
                            {e.details_json ?? "(no details)"}
                          </pre>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <h2 className="mb-2 flex items-center gap-2 font-serif text-lg text-foreground">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          Idempotency ledger
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">
          The 100 most recent Stripe event ids stored in
          <code className="mx-1 rounded bg-muted px-1">processed_stripe_events</code>
          — any repeat delivery of these ids short-circuits without mutating
          state or resending email.
        </p>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-start text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Processed at</th>
                <th className="px-3 py-2">Event type</th>
                <th className="px-3 py-2">Stripe event id</th>
              </tr>
            </thead>
            <tbody>
              {processed.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    No processed events yet.
                  </td>
                </tr>
              ) : (
                processed.map((p) => (
                  <tr
                    key={p.event_id}
                    className="border-t border-border hover:bg-muted/30"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                      {formatTs(p.processed_at)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {p.event_type}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                      {p.event_id}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`text-lg font-semibold ${tone ?? "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}
